import React from 'react';
import clsx from 'clsx';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { useNode } from '@craftjs/core';
import { useAppDispatch } from '@/store/hooks';
import { validateRedirectUrl } from '@/utils/countdownValidation';
import type { InteractionEvent } from '@builder4/shared/types/craft';
import {
  EXTERNAL_LINK_EVENT,
  STAY_ON_PAGE,
  SELECT_PAGE_EVENT,
} from '@builder4/shared/utils/constants';
import type { CountdownProps } from '@builder4/shared/components/Countdown';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useInteractionEvent } from '@/hooks/useInteractionEvent';
import { PageSelectField, ExternalUrlField } from '@/components/dropdowns/shared';

interface RedirectProps {
  className: string;
  text: string;
}

export const Redirect: React.FC<RedirectProps> = ({ className = '', text = 'Redirect' }) => {
  const dispatch = useAppDispatch();
  const {
    actions: { setProp },
    displayName,
    props,
  } = useNode((node) => {
    return {
      node,
      props: node.data.props,
      displayName: node.data.displayName,
    };
  });

  const { setBPProp, getBPProp } = useBreakpointAwareProp(displayName);

  const expireEvent = getBPProp(props, 'expireEvent');

  const { eventType, optionId, externalUrl, pages, isPagesLoading, constructLinkedPageUrl } =
    useInteractionEvent({
      event: expireEvent,
      defaultEventType: STAY_ON_PAGE,
    });

  const handleChangeEventType = (newValue: string) => {
    const data =
      newValue === STAY_ON_PAGE
        ? null
        : {
            url: '',
            optionId: '',
            newTab: false,
            useNofollow: false,
          };
    const newEvent = {
      eventType: newValue,
      eventData: data,
    } as InteractionEvent;

    setProp((props: CountdownProps) => {
      setBPProp(props, 'expireEvent', newEvent);
    });
  };

  const handlePageChange = (pageId: string) => {
    setProp((props: CountdownProps) => {
      const currentEvent = getBPProp(props, 'expireEvent');
      const newEvent = {
        ...currentEvent,
        eventData: {
          ...(currentEvent?.eventData || {}),
          optionId: pageId,
          url: constructLinkedPageUrl(pageId),
        },
      } as InteractionEvent;
      setBPProp(props, 'expireEvent', newEvent);
    });
  };

  const handleChangeUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProp((props: CountdownProps) => {
      const currentEvent = getBPProp(props, 'expireEvent');
      const newEvent = {
        ...currentEvent,
        eventData: {
          ...(currentEvent?.eventData || {}),
          url: value,
        },
      } as InteractionEvent;
      setBPProp(props, 'expireEvent', newEvent);
    });
  };

  const handleSanitizeUrl = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Validate and sanitize URL (Redirect-specific validation)
    const { isValid, sanitizedUrl } = validateRedirectUrl(value, dispatch);

    if (!isValid) {
      return;
    }

    // Update the node props with sanitized value
    setProp((props: CountdownProps) => {
      const currentEvent = getBPProp(props, 'expireEvent');
      const newEvent = {
        ...currentEvent,
        eventData: {
          ...(currentEvent?.eventData || {}),
          url: sanitizedUrl,
        },
      } as InteractionEvent;
      setBPProp(props, 'expireEvent', newEvent);
    });
  };

  return (
    <div className={clsx('flex w-full flex-col items-start gap-5', className)}>
      <div className="flex w-full items-center justify-between gap-3">
        {text !== '' && <p className="text-button-sm text-neutral-500">{text}</p>}

        <SelectField
          className="w-full max-w-[160px]"
          size="large"
          defaultValue={eventType}
          onValueChange={handleChangeEventType}
          placeholder=""
          text=""
        >
          <SelectItem value={STAY_ON_PAGE}>Stay on page</SelectItem>
          <SelectItem value={SELECT_PAGE_EVENT}>Select page</SelectItem>
          <SelectItem value={EXTERNAL_LINK_EVENT}>Open external link</SelectItem>
        </SelectField>
      </div>

      {eventType === SELECT_PAGE_EVENT && (
        <div className="flex w-full flex-col items-start gap-3">
          <PageSelectField
            optionId={optionId}
            pages={pages}
            isPagesLoading={isPagesLoading}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {eventType === EXTERNAL_LINK_EVENT && (
        <div className="flex w-full flex-col items-start gap-3">
          <ExternalUrlField
            externalUrl={externalUrl}
            onChangeUrl={handleChangeUrl}
            onSanitizeUrl={handleSanitizeUrl}
          />
        </div>
      )}
    </div>
  );
};
